import React, { useState } from 'react';
import { useFlutterwave, closePaymentModal } from 'flutterwave-react-v3';

// Flutterwave API Keys
const FLW_PUBLIC_KEY = process.env.REACT_APP_FLW_PUBLIC_KEY || 'FLWPUBK_TEST-83c552bfa1d2d6903d4fc7a2f549815d-X';
const FLW_SECRET_KEY = process.env.REACT_APP_FLW_SECRET_KEY || 'FLWSECK_TEST-407662a090b655c24ba9a0a615426b3d-X';

// Supabase Edge Function URL for booking
const SUPABASE_FUNCTION_URL = 'https://wnsyglbyunngvlcpdhju.supabase.co/functions/v1';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Induc3lnbGJ5dW5uZ3ZsY3BkaGp1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjU4OTUyMDEsImV4cCI6MjA4MTQ3MTIwMX0.qgSiHbYCWqo6qS9ZAsvwxk6aRr0Yq3LQnEp7XnRMHAc';

const FlutterwavePaymentForm = ({ amount, flight, passengerData, onSuccess, onError }) => {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Extract numeric amount from string (e.g., "₦850,000" -> 850000)
  // Amount is always in NGN for Flutterwave
  const getAmount = () => {
    if (typeof amount === 'string') {
      // Remove all currency symbols and commas, then parse
      const numericAmount = parseFloat(amount.replace(/[₦$£€,]/g, '')) || 0;
      return Math.round(numericAmount); // Flutterwave expects whole numbers (kobo/cent equivalent)
    }
    return Math.round(parseFloat(amount) || 0);
  };

  // Format phone number to E.164 format
  const formatPhoneNumber = (phone, countryCode = null) => {
    // If phone is empty or just a country code, use default
    if (!phone || phone === '' || phone === null || phone === undefined) {
      return countryCode ? countryCode + '8012345678' : '+2348012345678';
    }
    
    let phoneStr = String(phone).trim();
    
    // If phone already starts with +, check if it's complete
    if (phoneStr.startsWith('+')) {
      const digitsOnly = phoneStr.replace(/[^\d]/g, '');
      // If it's too short (just country code like +254, +234), it's incomplete
      if (digitsOnly.length <= 4) {
        // It's just a country code, use the country code selector and add default number
        if (countryCode) {
          phoneStr = countryCode.replace('+', '') + '8012345678';
        } else {
          phoneStr = '2348012345678'; // Default Nigeria
        }
      } else {
        // It's a complete number, use it as-is
        phoneStr = digitsOnly;
      }
    } else {
      // Remove all non-digits
      phoneStr = phoneStr.replace(/[^\d]/g, '');
      
      // Remove leading zeros
      phoneStr = phoneStr.replace(/^0+/, '');
      
      // If it starts with a country code (like 234, 254), check if we should use it or the selector
      if (phoneStr.startsWith('234') && phoneStr.length >= 13) {
        // Nigerian number with country code
        phoneStr = phoneStr;
      } else if (phoneStr.startsWith('254') && phoneStr.length >= 12) {
        // Kenyan number with country code
        phoneStr = phoneStr;
      } else if (countryCode) {
        // Use country code from selector and append the number
        const codeDigits = countryCode.replace('+', '').replace(/[^\d]/g, '');
        // Remove country code from phone if it's already there
        if (phoneStr.startsWith(codeDigits)) {
          phoneStr = phoneStr.substring(codeDigits.length);
        }
        phoneStr = codeDigits + phoneStr;
      } else {
        // Default to Nigeria
        phoneStr = '234' + phoneStr;
      }
    }
    
    // Ensure it starts with +
    if (!phoneStr.startsWith('+')) {
      phoneStr = '+' + phoneStr;
    }
    
    // Validate E.164 format: + followed by 1-15 digits (first digit after + must be 1-9)
    if (!/^\+[1-9]\d{1,14}$/.test(phoneStr)) {
      // Try to fix - remove leading zeros after +
      phoneStr = phoneStr.replace(/^\+0+/, '+');
      
      if (!/^\+[1-9]\d{1,14}$/.test(phoneStr)) {
        // Still invalid, use default
        return countryCode ? countryCode + '8012345678' : '+2348012345678';
      }
    }
    
    return phoneStr;
  };

  // Calculate passenger type based on date of birth
  const calculatePassengerType = (dateOfBirth) => {
    if (!dateOfBirth) {
      return 'adult'; // Default to adult if no date of birth
    }
    
    try {
      const birthDate = new Date(dateOfBirth);
      const today = new Date();
      
      // Validate date
      if (isNaN(birthDate.getTime()) || birthDate > today) {
        console.warn('[FlutterwavePaymentForm] Invalid date of birth:', dateOfBirth);
        return 'adult'; // Invalid date, default to adult
      }
      
      // Calculate age in years
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const dayDiff = today.getDate() - birthDate.getDate();
      
      // Adjust age if birthday hasn't occurred this year
      if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        age--;
      }
      
      // Calculate age in days for more accurate infant detection
      const ageInDays = Math.floor((today - birthDate) / (1000 * 60 * 60 * 24));
      const ageInMonths = Math.floor(ageInDays / 30);
      
      console.log('[FlutterwavePaymentForm] Age calculation:', {
        dateOfBirth,
        ageInYears: age,
        ageInMonths,
        ageInDays,
        calculatedType: ageInMonths < 24 ? 'infant_without_seat' : (age < 12 ? 'child' : 'adult')
      });
      
      // Infant: under 2 years (24 months)
      if (ageInMonths < 24 || age < 2) {
        return 'infant_without_seat';
      }
      // Child: 2-11 years
      else if (age < 12) {
        return 'child';
      }
      // Adult: 12+ years
      else {
        return 'adult';
      }
    } catch (error) {
      console.error('[FlutterwavePaymentForm] Error calculating passenger type:', error);
      return 'adult'; // Default to adult on error
    }
  };

  // Prepare passenger data for booking - Match StripePaymentForm format exactly
  const preparePassengers = () => {
    // Split name helper (same as StripePaymentForm)
    const splitName = (fullName) => {
      if (!fullName) return { first: 'John', last: 'Doe' };
      const parts = fullName.trim().split(/\s+/);
      if (parts.length === 1) return { first: parts[0], last: 'Doe' };
      return { first: parts[0], last: parts.slice(1).join(' ') };
    };

    const nameParts = splitName(passengerData?.fullName || passengerData?.firstName);
    
    // Format title helper (same as StripePaymentForm)
    const formatTitle = (title) => {
      if (!title || title === '' || title === null || title === undefined) {
        return 'mr';
      }
      const titleLower = String(title).toLowerCase().trim();
      if (titleLower === 'mr' || titleLower === 'mister') return 'mr';
      if (titleLower === 'mrs' || titleLower === 'missus') return 'mrs';
      if (titleLower === 'ms') return 'ms';
      if (titleLower === 'miss') return 'miss';
      return titleLower;
    };

    // Format gender helper (same as StripePaymentForm)
    const formatGender = (gender) => {
      if (!gender) return 'm';
      const genderLower = String(gender).toLowerCase().trim();
      if (genderLower === 'male' || genderLower === 'm' || genderLower === 'man') return 'm';
      if (genderLower === 'female' || genderLower === 'f' || genderLower === 'woman') return 'f';
      return 'm';
    };

    // Get phone number - SAME LOGIC AS StripePaymentForm
    // Use phone or phoneNumber field, fallback to default
    let rawPhone = passengerData?.phone || passengerData?.phoneNumber || '+2348012345678';
    
    // If phone number is too short or invalid, use default
    const digitsOnly = rawPhone.replace(/[^\d]/g, '');
    if (!rawPhone || rawPhone === '' || digitsOnly.length < 10) {
      rawPhone = '+2348012345678'; // Default Nigerian number
    }
    
    // Ensure it's in E.164 format (starts with +)
    if (!rawPhone.startsWith('+')) {
      // Add country code if missing
      const phoneCountryCode = passengerData?.phoneCountryCode || passengerData?.countryCode || '+234';
      rawPhone = phoneCountryCode.replace('+', '') + rawPhone.replace(/^0+/, '');
      rawPhone = '+' + rawPhone;
    }

    // Prepare passengers array - SAME FORMAT AS StripePaymentForm
    const passengers = passengerData && Object.keys(passengerData).length > 0
      ? [{
          type: 'adult', // Always send 'adult', backend will recalculate based on born_on
          given_name: passengerData.firstName || nameParts.first,
          family_name: passengerData.lastName || nameParts.last,
          email: passengerData.email || 'customer@example.com',
          phone_number: rawPhone, // Send raw phone, backend will format it
          title: formatTitle(passengerData.title),
          gender: formatGender(passengerData.gender),
          // Only include born_on if provided and valid (not in future)
          ...(passengerData.dateOfBirth && (() => {
            const birthDate = new Date(passengerData.dateOfBirth);
            const today = new Date();
            today.setHours(23, 59, 59, 999);
            if (birthDate <= today) {
              return { born_on: passengerData.dateOfBirth };
            }
            return {};
          })())
        }]
      : [{
          type: 'adult',
          given_name: 'John',
          family_name: 'Doe',
          email: 'customer@example.com',
          phone_number: '+2348012345678',
          title: 'mr',
          gender: 'm'
        }];

    return passengers;
  };

  // Create booking with Duffel API after payment success
  const createBooking = async (paymentReference) => {
    try {
      const offerId = flight.id || flight.offerId;
      
      if (!offerId) {
        throw new Error('No flight offer ID found. Please search for flights first.');
      }

      if (!offerId.toString().startsWith('off_')) {
        console.warn('Invalid Duffel offer ID:', offerId, '- Skipping booking creation');
        return null;
      }

      const passengers = preparePassengers();
      
      // Prepare contacts - SAME FORMAT AS StripePaymentForm
      const contacts = [{
        email: passengers[0].email,
        phone_number: passengers[0].phone_number // Use same phone as passenger
      }];
      
      console.log('[FlutterwavePaymentForm] Formatted passenger data:', {
        type: passengers[0].type,
        given_name: passengers[0].given_name,
        family_name: passengers[0].family_name,
        email: passengers[0].email,
        phone_number: passengers[0].phone_number,
        phone_number_length: passengers[0].phone_number?.length,
        title: passengers[0].title,
        gender: passengers[0].gender,
        born_on: passengers[0].born_on,
        contact_phone: contacts[0].phone_number
      });

      const bookingEndpoint = SUPABASE_FUNCTION_URL.includes('supabase.co')
        ? `${SUPABASE_FUNCTION_URL}/create-booking`
        : 'http://localhost:3001/api/bookings/create';

      const bookingHeaders = {
        'Content-Type': 'application/json',
      };

      if (bookingEndpoint.includes('supabase.co')) {
        if (SUPABASE_ANON_KEY) {
          bookingHeaders['apikey'] = SUPABASE_ANON_KEY;
          bookingHeaders['Authorization'] = `Bearer ${SUPABASE_ANON_KEY}`;
        }
      }

      console.log('[FlutterwavePaymentForm] Creating booking with Duffel API...');
      console.log('[FlutterwavePaymentForm] Offer ID:', offerId);
      console.log('[FlutterwavePaymentForm] Passengers:', passengers);
      console.log('[FlutterwavePaymentForm] Payment Reference:', paymentReference);

      const bookingResponse = await fetch(bookingEndpoint, {
        method: 'POST',
        headers: bookingHeaders,
        body: JSON.stringify({
          offerId: offerId,
          passengers: passengers,
          contacts: contacts,
          paymentType: 'balance', // Use balance for test mode (Flutterwave payment already processed)
          flutterwaveReference: paymentReference
        }),
      });

      const bookingData = await bookingResponse.json();

      if (bookingData.success && bookingData.booking) {
        console.log('[FlutterwavePaymentForm] ========== BOOKING CONFIRMED IN DUFFEL ==========');
        console.log('[FlutterwavePaymentForm] Order ID:', bookingData.booking.id);
        console.log('[FlutterwavePaymentForm] Booking Reference:', bookingData.booking.bookingReference);
        console.log('[FlutterwavePaymentForm] Duffel Order ID:', bookingData.booking.duffelOrderId);
        console.log('[FlutterwavePaymentForm] Status:', bookingData.booking.status);
        console.log('[FlutterwavePaymentForm] ======================================');
        return bookingData.booking;
      }

      throw new Error(bookingData.error || 'Booking creation failed');
    } catch (error) {
      console.error('[FlutterwavePaymentForm] Booking creation error:', error);
      throw error;
    }
  };

  const config = {
    public_key: FLW_PUBLIC_KEY,
    tx_ref: `FLW-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    amount: getAmount(),
    currency: 'NGN',
    payment_options: 'card,ussd,banktransfer,account',
    customer: {
      email: passengerData?.email || 'customer@example.com',
      phone_number: passengerData?.phoneNumber || passengerData?.phone || '+2348012345678',
      name: passengerData?.fullName || 'Customer Name',
    },
    customizations: {
      title: 'Flight Booking Payment',
      description: `Payment for flight from ${flight.departureAirport} to ${flight.arrivalAirport}`,
      logo: 'https://wnsyglbyunngvlcpdhju.supabase.co/storage/v1/object/public/Images/1-01.png',
    },
    callback: async (response) => {
      console.log('[FlutterwavePaymentForm] ========== PAYMENT RESPONSE ==========');
      console.log('[FlutterwavePaymentForm] Payment response:', response);
      console.log('[FlutterwavePaymentForm] Status:', response.status);
      console.log('[FlutterwavePaymentForm] Transaction ID:', response.transaction_id);
      console.log('[FlutterwavePaymentForm] Transaction Reference:', response.tx_ref);
      console.log('[FlutterwavePaymentForm] ======================================');
      
      if (response.status === 'successful') {
        setProcessing(true);
        try {
          console.log('[FlutterwavePaymentForm] Payment successful! Creating booking in Duffel...');
          
          // Create booking in Duffel after successful payment
          const booking = await createBooking(response.tx_ref);
          
          if (booking) {
            console.log('[FlutterwavePaymentForm] ✅ Booking created successfully!');
            console.log('[FlutterwavePaymentForm] Navigating to success page...');
            
            // Call onSuccess to navigate to thank you page
            onSuccess({
              booking: booking,
              payment: {
                reference: response.tx_ref,
                transactionId: response.transaction_id,
                paymentMethod: 'flutterwave',
                amount: response.amount,
                currency: response.currency,
                status: 'succeeded'
              },
              message: 'Payment and booking successful!'
            });
          } else {
            console.error('[FlutterwavePaymentForm] ❌ Booking creation returned null');
            // Payment succeeded but booking failed
            onSuccess({
              booking: null,
              payment: {
                reference: response.tx_ref,
                transactionId: response.transaction_id,
                paymentMethod: 'flutterwave',
                amount: response.amount,
                currency: response.currency,
                status: 'succeeded'
              },
              message: 'Payment successful, but booking could not be created. Please contact support.'
            });
          }
        } catch (error) {
          console.error('[FlutterwavePaymentForm] ❌ Error after payment:', error);
          console.error('[FlutterwavePaymentForm] Error details:', error.message);
          
          // Show error but don't redirect - let user see the error
          setError('Payment successful but booking failed: ' + error.message + '. Please contact support with reference: ' + response.tx_ref);
          onError('Payment successful but booking failed. Please contact support with reference: ' + response.tx_ref);
          
          // Still navigate to success page but with error message
          // This way user knows payment succeeded but booking needs manual intervention
          onSuccess({
            booking: null,
            payment: {
              reference: response.tx_ref,
              transactionId: response.transaction_id,
              paymentMethod: 'flutterwave',
              amount: response.amount,
              currency: response.currency,
              status: 'succeeded'
            },
            message: 'Payment successful, but booking could not be created automatically. Please contact support with reference: ' + response.tx_ref,
            error: error.message
          });
        } finally {
          setProcessing(false);
        }
      } else {
        console.error('[FlutterwavePaymentForm] ❌ Payment was not successful');
        onError('Payment was not successful. Please try again.');
      }
      closePaymentModal();
    },
    onclose: () => {
      console.log('[FlutterwavePaymentForm] Payment modal closed');
      setProcessing(false);
    },
  };

  const handleFlutterPayment = useFlutterwave(config);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setProcessing(true);

    try {
      // Validate required fields
      if (!passengerData?.email) {
        throw new Error('Email is required');
      }

      if (!flight?.id && !flight?.offerId) {
        throw new Error('Flight information is missing');
      }

      // Open Flutterwave payment modal
      handleFlutterPayment({
        callback: config.callback,
        onclose: config.onclose,
      });
    } catch (err) {
      setError(err.message);
      onError(err.message);
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flutterwave-payment-form">
      {error && (
        <div className="payment-error">
          <p>{error}</p>
        </div>
      )}

      <div className="payment-info">
        <div className="payment-summary">
          <div className="summary-row">
            <span className="summary-label">Amount:</span>
            <span className="summary-value">{amount}</span>
          </div>
          <div className="summary-row">
            <span className="summary-label">Payment Method:</span>
            <span className="summary-value">Flutterwave</span>
          </div>
        </div>

        <div className="payment-features">
          <div className="feature-item">
            <span className="feature-icon">💳</span>
            <span>Card, Bank Transfer, USSD, Account</span>
          </div>
          <div className="feature-item">
            <span className="feature-icon">🔒</span>
            <span>Secure Payment</span>
          </div>
          <div className="feature-item">
            <span className="feature-icon">⚡</span>
            <span>Instant Processing</span>
          </div>
        </div>
      </div>

      <button 
        type="submit" 
        className="pay-button"
        disabled={processing}
      >
        {processing ? 'Processing...' : `Pay ${amount} with Flutterwave`}
      </button>

      <div className="payment-note">
        <p>You will be redirected to Flutterwave's secure payment page to complete your transaction.</p>
      </div>
    </form>
  );
};

export default FlutterwavePaymentForm;
